// Algebraic data model for the block editor.
//
// Every Strudel construct we support is one of a closed set of variants
// (Statement / Expression / Effect / Numeric / MiniNode). The registry
// (see src/blocks/registry) declares how each variant renders, prints,
// and parses. Adding a new Strudel method is: add a kind here, add a
// registry entry. Parser, codegen, and UI follow.
//
// Phase 1 notes:
// - Effects, modulators, and mini-notation node kinds are declared in
//   full, but only a subset have registry entries in this first
//   installment (just enough to round-trip the default songs). Future
//   phases add the rest without changing this file.
// - Pattern strings on voice blocks are plain strings in Phase 1 — the
//   mini-notation sub-editor lands in Phase 2, at which point the
//   `pattern` field becomes `MiniPattern | { kind: 'raw'; source }`.

import { nanoid } from 'nanoid';

/** Block IDs are stable within a session; re-parse from code mints fresh ones. */
export type BlockId = string;

export const newBlockId = (): BlockId => nanoid(10);

/** Character offsets in the original source. Populated by the parser,
 * ignored by the code generator. Consumers use it for
 * click-block → highlight-source interactions. */
export type SourceRange = { from: number; to: number };

// ─── Numeric expressions ────────────────────────────────────────────
// Appear wherever Strudel accepts a number: setcpm(…), .gain(…),
// arrange([N, …]), modulator ranges, etc.

export type Numeric =
  | { kind: 'num'; value: number }
  | { kind: 'binary'; op: '+' | '-' | '*' | '/'; lhs: Numeric; rhs: Numeric }
  | { kind: 'ref'; name: string }
  | {
      kind: 'modulator';
      source: ModulatorSource;
      // Some modulators carry positional args (e.g. knob(N) → cc number).
      // Nullary modulators leave this undefined.
      args?: Numeric[];
      range?: [Numeric, Numeric];
      slow?: Numeric;
      fast?: Numeric;
    }
  // Arg-level escape hatch for expressions we can't yet model. The
  // only place the "no escape hatch" rule bends — documented trade-off.
  | { kind: 'raw'; source: string };

export type ModulatorSource =
  | 'sine'
  | 'cosine'
  | 'square'
  | 'saw'
  | 'tri'
  | 'rand'
  | 'irand'
  | 'perlin'
  // MIDI-input signal: knob(N, channel?). Args are [cc, channel?].
  | 'knob';

// ─── Mini-notation ──────────────────────────────────────────────────
// Not rendered visually in Phase 1 (pattern fields are plain strings);
// types declared here so Phase 2's sub-editor slots in without touching
// downstream code.

export type MiniNode =
  // `atom` covers every leaf token — a sample name (`bd`, `hh:2`), a note
  // name (`a3`, `eb2`), or a chord symbol (`Dm7`). Whether it's heard as a
  // sample or a pitch is determined by the surrounding voice kind
  // (voice.s vs voice.note), not by the token itself.
  | { kind: 'atom'; id: BlockId; name: string; index?: number }
  | { kind: 'rest'; id: BlockId }
  | { kind: 'repeat'; id: BlockId; inner: MiniNode; count: Numeric }
  | { kind: 'slow'; id: BlockId; inner: MiniNode; factor: Numeric }
  | { kind: 'weight'; id: BlockId; inner: MiniNode; weight: Numeric }
  | { kind: 'bang'; id: BlockId; inner: MiniNode; count: Numeric }
  | { kind: 'group'; id: BlockId; children: MiniNode[] }
  | { kind: 'choice'; id: BlockId; children: MiniNode[] }
  | { kind: 'parallel'; id: BlockId; children: MiniNode[] }
  | { kind: 'polymeter'; id: BlockId; children: MiniNode[]; steps?: Numeric }
  | { kind: 'euclid'; id: BlockId; inner: MiniNode; pulses: Numeric; steps: Numeric; rot?: Numeric };

export type MiniPattern = MiniNode[];

// A pattern slot is either a structured mini-notation tree (when the
// string parsed cleanly) or a raw string (fallback for grammar we
// haven't taught the mini parser yet). Mini slots regenerate their
// source from the tree on demand so edits to the tree are always
// visible downstream without the caller having to sync a stored copy.
export type PatternSlot =
  | { kind: 'raw'; source: string }
  | { kind: 'mini'; nodes: MiniPattern };

// ─── Effects (chained method calls on a voice) ──────────────────────

// Simple effects — method calls with positional args applied directly
// to the voice. Phase 1 vocab + Phase 3's structural transforms.
export type SimpleEffectKind =
  | 'gain'
  | 'speed'
  | 'pan'
  | 'lpf'
  | 'lpq'
  | 'hpf'
  | 'hpq'
  | 's' // .s("sawtooth") applied to a note(…) voice
  | 'attack'
  | 'release'
  | 'delay'
  | 'delaytime'
  | 'delayfb'
  | 'struct'
  | 'slow'
  | 'fast'
  | 'rev'
  | 'palindrome'
  | 'ply'
  | 'stutter'
  | 'iter'
  | 'iterBack'
  | 'early'
  | 'late'
  | 'linger'
  | 'zoom'
  | 'compress'
  // Phase 4 tonal / harmony. Some take strings (scale, chord, arp)
  // which route through the same string-arg path as .s(); others take
  // numerics (transpose, strans, add, sub, mul) or two numerics
  // (bjork — euclidean by value), or no args (voicing).
  | 'scale'
  | 'chord'
  | 'voicing'
  | 'arp'
  | 'transpose'
  | 'strans'
  | 'bjork'
  | 'add'
  | 'sub'
  | 'mul'
  // Phase 5 long-tail vocab. Pure additive — every one of these
  // slots into the numeric-arg dispatch. Grouped by family for
  // readability.
  // Filter / EQ
  | 'bpf'
  | 'bpq'
  | 'djf'
  | 'crush'
  | 'coarse'
  | 'distort'
  | 'shape'
  | 'squiz'
  | 'comb'
  | 'phaser'
  | 'phaserdepth'
  | 'phaserfreq'
  | 'phaserrate'
  // Envelope / amplitude
  | 'decay'
  | 'sustain'
  | 'velocity'
  | 'amp'
  | 'clip'
  | 'accelerate'
  | 'dough'
  // Sampler / oscillator controls
  | 'n'
  | 'orbit'
  | 'octave'
  | 'cut'
  | 'begin'
  | 'end'
  | 'unison'
  | 'detune'
  | 'chorus'
  | 'noise'
  // Reverb / space
  | 'room'
  | 'roomsize'
  | 'predelay'
  | 'size'
  | 'hcut'
  | 'lcut'
  | 'dry'
  // Phase 5 long-tail: pattern-string args (vowel, adsr, ad)
  | 'vowel'
  | 'adsr'
  | 'ad'
  // Phase 5 long-tail: sample loop + slicing controls
  | 'loop'
  | 'loopBegin'
  | 'loopEnd'
  | 'loopFirst'
  | 'chop'
  | 'striate'
  | 'striateBy'
  | 'fit'
  | 'fitslice'
  | 'slice'
  // Phase 6 backfill — pattern-level transforms that round-trip into
  // the existing simple-effect shape (numeric args only, no body).
  // `mask` is the second pattern-arg effect alongside `struct`.
  | 'echo'
  | 'swing'
  | 'swingBy'
  | 'degrade'
  | 'degradeBy'
  | 'undegrade'
  | 'undegradeBy'
  | 'loopAt'
  | 'pace'
  | 'repeatCycles'
  | 'mask';

// Function-body effects — take an optional numeric arg plus a
// pattern-transform body expressed in source as an arrow function:
// `.every(4, x => x.fast(2))`, `.off(0.125, x => x.rev())`. The body
// is a chain of effects applied to a transformed copy of the voice.
// "Conditional" here is a loose umbrella — covers probabilistic
// (sometimes/often/…), cycle-counted (every/when), offsetting (off),
// chunking (chunk), and channel-panning (jux/juxBy) — all share the
// same structural parse/codegen shape.
export type ConditionalKind =
  | 'every'
  | 'sometimes'
  | 'often'
  | 'rarely'
  | 'almostAlways'
  | 'almostNever'
  | 'always'
  | 'never'
  | 'when'
  | 'off'
  | 'chunk'
  | 'jux'
  | 'juxBy'
  // Phase 6 backfill — body-taking transforms. `superimpose` and
  // `someCycles` carry no leading numeric; `inside`/`outside`/
  // `firstOf`/`lastOf`/`someCyclesBy` carry one; `whenmod` and
  // `echoWith` carry two.
  | 'superimpose'
  | 'someCycles'
  | 'someCyclesBy'
  | 'inside'
  | 'outside'
  | 'firstOf'
  | 'lastOf'
  | 'whenmod'
  | 'echoWith';

export type EffectKind = SimpleEffectKind | ConditionalKind;

export type EffectArg =
  | { kind: 'numeric'; value: Numeric }
  | { kind: 'pattern'; value: PatternSlot }
  | { kind: 'string'; value: string };

export type SimpleEffect = {
  id: BlockId;
  kind: SimpleEffectKind;
  args: EffectArg[];
  sourceRange?: SourceRange;
};

export type ConditionalEffect = {
  id: BlockId;
  kind: ConditionalKind;
  // Leading numeric/probability args — `.every(4, …)` has one,
  // `.sometimes(…)` has zero. Never contains the body.
  condArgs: EffectArg[];
  // The method chain inside the arrow function body.
  body: Effect[];
  sourceRange?: SourceRange;
};

export type Effect = SimpleEffect | ConditionalEffect;

// Compile-time exhaustive — adding to ConditionalKind without
// updating this table is a type error.
const CONDITIONAL_KIND_LOOKUP: Record<ConditionalKind, true> = {
  every: true,
  sometimes: true,
  often: true,
  rarely: true,
  almostAlways: true,
  almostNever: true,
  always: true,
  never: true,
  when: true,
  off: true,
  chunk: true,
  jux: true,
  juxBy: true,
  superimpose: true,
  someCycles: true,
  someCyclesBy: true,
  inside: true,
  outside: true,
  firstOf: true,
  lastOf: true,
  whenmod: true,
  echoWith: true,
};

export function isConditionalKind(name: string): name is ConditionalKind {
  return Object.prototype.hasOwnProperty.call(CONDITIONAL_KIND_LOOKUP, name);
}

export function isConditionalEffect(e: Effect): e is ConditionalEffect {
  return isConditionalKind(e.kind);
}

// ─── Expressions — things that live inside a const or a stack ───────

export type VoiceBlock = {
  id: BlockId;
  kind: 'voice.s' | 'voice.note';
  pattern: PatternSlot;
  effects: Effect[];
  sourceRange?: SourceRange;
};

export type StackBlock = {
  id: BlockId;
  kind: 'stack';
  children: Expression[];
  sourceRange?: SourceRange;
};

export type ArrangeEntry = {
  id: BlockId;
  cycles: Numeric;
  section: Expression;
  sourceRange?: SourceRange;
};

export type ArrangeBlock = {
  id: BlockId;
  kind: 'arrange';
  entries: ArrangeEntry[];
  sourceRange?: SourceRange;
};

export type RefBlock = {
  id: BlockId;
  kind: 'ref';
  name: string;
  /**
   * Method chain applied to the reference — e.g., `bass.lpf(250)` inside
   * `stack(bass.lpf(250), pad)`. Empty (or undefined) when the reference
   * appears bare. Strudel chains compose functionally: calling `.lpf()`
   * on a pattern returns a new pattern, so a ref-with-effects is a
   * distinct voice derived from the referenced binding.
   */
  effects?: Effect[];
  sourceRange?: SourceRange;
};

// Arbitrary JS (arrow function bound to a const, etc.). Preserved
// verbatim, opaque to block-level editing.
export type CustomFunctionBlock = {
  id: BlockId;
  kind: 'customFunction';
  source: string;
  sourceRange?: SourceRange;
};

export type Expression =
  | VoiceBlock
  | StackBlock
  | ArrangeBlock
  | RefBlock
  | CustomFunctionBlock;

// ─── Statements — top-level constructs in a song ────────────────────

export type StatementComments = {
  leadingComment?: string;
  trailingComment?: string;
};

export type StatementMeta = StatementComments & {
  sourceRange?: SourceRange;
};

export type Statement =
  | ({ id: BlockId; kind: 'setcpm' | 'setcps'; value: Numeric } & StatementMeta)
  | ({ id: BlockId; kind: 'const'; name: string; expr: Expression } & StatementMeta)
  | ({ id: BlockId; kind: 'expression'; expr: Expression } & StatementMeta)
  // `samples('github:user/repo')` — sound-pack loader. Side-effecting
  // call that registers sample names for `s("…")` voices to reference.
  // Source string only; object-literal form (`samples({…})`) lands later.
  | ({ id: BlockId; kind: 'samples'; source: string } & StatementMeta)
  // A free-floating comment: one or more comment lines with a blank
  // line on at least one side (or at end of file). Renders as its own
  // block so round-trips don't glue section-divider comments onto the
  // next statement.
  | ({ id: BlockId; kind: 'comment'; content: string } & StatementMeta);

// ─── Song ───────────────────────────────────────────────────────────

export type Song = {
  statements: Statement[];
};

// ─── Parse results ──────────────────────────────────────────────────

export type ParseOk = { ok: true; song: Song };

export type ParseError = {
  ok: false;
  reason: string;
  location: { from: number; to: number };
};

export type ParseResult = ParseOk | ParseError;

// ─── Type guards ────────────────────────────────────────────────────

export function isVoice(expr: Expression): expr is VoiceBlock {
  return expr.kind === 'voice.s' || expr.kind === 'voice.note';
}

export function isContainer(expr: Expression): expr is StackBlock | ArrangeBlock {
  return expr.kind === 'stack' || expr.kind === 'arrange';
}
